import React, { useState, useEffect } from 'react';
import {
    Search,
    Loader2,
    User,
    BookOpen,
    Calendar,
    DollarSign,
    Tag,
    Layers,
    ChevronRight,
    Filter
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const CourseOrders = ({ authToken }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await fetch(`${API_BASE_URL}/admin/super/course-orders`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const list = Array.isArray(data) ? data : (data.course_orders || data.orders || []);
                    setOrders(list);
                } else {
                    const errData = await response.json().catch(() => ({}));
                    setError(errData.error || 'Failed to load course orders');
                    setOrders([]);
                }
            } catch (err) {
                setError('Failed to fetch course bookings');
                setOrders([]);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, [authToken]);

    const getCustomerDisplay = (o) => {
        if (!o) return '—';
        const name = o.customer_name ?? o.name ?? o.user_name ?? o.buyer_name;
        if (name && String(name).trim()) return String(name).trim();
        const first = o.first_name ?? o.billing_first_name;
        const last = o.last_name ?? o.billing_last_name;
        const combined = [first, last].filter(Boolean).map(String).join(' ').trim();
        if (combined) return combined;
        return o.customer_email ?? o.email ?? '—';
    };

    const filteredOrders = orders.filter(o => {
        const courseName = (o.course_name || o.item_name || '').toLowerCase();
        const customerName = getCustomerDisplay(o).toLowerCase();
        const categoryName = (o.category_name || o.category || '').toLowerCase();
        const term = searchTerm.toLowerCase();
        return courseName.includes(term) || customerName.includes(term) || categoryName.includes(term) || (o.order_id || o.id || '').toString().toLowerCase().includes(term);
    });

    const categoryCounts = filteredOrders.reduce((acc, o) => {
        const cat = o.category_name || o.category || 'Uncategorized';
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
    }, {});

    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, o) => {
        const value = o.amount || o.price || 0;
        const numericValue = typeof value === 'string'
            ? Number(value.replace(/[^0-9.]/g, ''))
            : Number(value);
        return sum + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="space-y-8 p-1">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Course Orders</h2>
                    <p className="text-sm text-gray-500 mt-1">Course purchases from the user panel (bookings).</p>
                </div>
            </div>
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>
            )}

            {/* Category Breakdown & Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 space-y-4">
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4">
                        <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                            <BookOpen className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Enrollments</p>
                            <p className="text-2xl font-black text-gray-900">{totalOrders}</p>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center space-x-4">
                        <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
                            <DollarSign className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Revenue</p>
                            <p className="text-2xl font-black text-gray-900">{formatCurrency(totalRevenue)}</p>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-widest flex items-center">
                            <Layers className="h-4 w-4 mr-2 text-indigo-500" />
                            Purchased Per Category
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {Object.entries(categoryCounts).length === 0 ? (
                            <p className="text-sm text-gray-400">No category data available.</p>
                        ) : (
                            Object.entries(categoryCounts).map(([cat, count]) => (
                                <div key={cat} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">{cat}</p>
                                    <p className="text-xl font-black text-gray-900">{count} <span className="text-xs font-medium text-gray-500">Books</span></p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by Course, Customer or Category..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    />
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg shadow-gray-100 border border-gray-100 overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="bg-gray-50/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">#</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Customer</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Course Name</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Category</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Amount</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Payment</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-widest">Date</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-50">
                                {filteredOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-12 text-center text-gray-400 font-medium">No course orders yet. Bookings from the user panel will appear here.</td>
                                    </tr>
                                ) : (
                                    filteredOrders.map((o, idx) => (
                                        <tr key={o.order_id || o.id || idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">{idx + 1}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-2">
                                                        <User className="h-4 w-4 text-gray-500" />
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900">{getCustomerDisplay(o)}</span>
                                                        {(o.customer_email || o.email) && (
                                                            <div className="text-xs text-gray-500">{o.customer_email ?? o.email}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm font-semibold text-gray-900">
                                                    <BookOpen className="h-4 w-4 text-gray-400 mr-2" />
                                                    {o.course_name || o.item_name || '—'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full bg-purple-50 text-purple-600 border border-purple-100">
                                                    {o.category_name || o.category || '—'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                {typeof o.amount === 'number' ? `₹${o.amount.toLocaleString('en-IN')}` : `₹${(o.amount || 0)}`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-full ${(o.payment_status || o.status || '').toLowerCase() === 'completed' || (o.payment_status || o.status || '').toLowerCase() === 'paid' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                                                    {o.payment_status || o.status || '—'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
                                                {o.order_date || o.created_at ? new Date(o.order_date || o.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CourseOrders;
